import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect, useCallback } from "react";
import { CheckCircle2, Circle, AlertTriangle, UtensilsCrossed, Loader2, ClipboardList, Sun, Cloud, Moon } from "lucide-react";
import { Link } from "react-router-dom";
import { scheduleSmartReminders } from "@/utils/notifications";
import { toast } from "sonner";

const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.06 } } };
const fadeUp = { hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: { duration: 0.35 } } };

const CONFETTI_COLORS = ["#22c55e", "#0ea5e9", "#f59e0b", "#ec4899", "#a855f7", "#14b8a6"];

const ConfettiBurst = ({ onDone }: { onDone: () => void }) => {
  const dots = Array.from({ length: 6 }, (_, i) => ({
    angle: (i / 6) * 360,
    color: CONFETTI_COLORS[i],
  }));
  return (
    <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
      {dots.map((dot, i) => {
        const rad = (dot.angle * Math.PI) / 180;
        const tx = Math.cos(rad) * 28;
        const ty = Math.sin(rad) * 28;
        return (
          <motion.div
            key={i}
            className="absolute h-2 w-2 rounded-full"
            style={{ backgroundColor: dot.color }}
            initial={{ x: 0, y: 0, opacity: 1, scale: 1 }}
            animate={{ x: tx, y: ty, opacity: 0, scale: 0.4 }}
            transition={{ duration: 0.55, ease: "easeOut" }}
            onAnimationComplete={i === 0 ? onDone : undefined}
          />
        );
      })}
    </div>
  );
};

const todayStr = () => new Date().toISOString().split("T")[0];

const SLOT_RE = /^\[(morning|midday|evening)\] /;
const getSlot = (text: string): "morning" | "midday" | "evening" | "general" => {
  if (text.startsWith("[morning]")) return "morning";
  if (text.startsWith("[midday]"))  return "midday";
  if (text.startsWith("[evening]")) return "evening";
  return "general";
};
const displayText = (text: string) =>
  text.replace(SLOT_RE, "").replace(/ — \d+\+\d+\+\d+.*$/, "");

const SLOT_META = {
  morning: { labelEn: "Morning", labelBn: "সকাল",  Icon: Sun,            color: "text-amber-500",  bg: "bg-amber-500/10"  },
  midday:  { labelEn: "Midday",  labelBn: "দুপুর",  Icon: Cloud,          color: "text-sky-500",    bg: "bg-sky-500/10"    },
  evening: { labelEn: "Night",   labelBn: "রাত",   Icon: Moon,           color: "text-indigo-400", bg: "bg-indigo-400/10" },
  general: { labelEn: "Tasks",   labelBn: "টাস্ক", Icon: UtensilsCrossed, color: "text-primary",   bg: "bg-primary/10"    },
} as const;

function processTasks(raw: any[]): any[] {
  const today = todayStr();
  const nowMs = Date.now();
  const ONE_DAY_MS = 1000 * 60 * 60 * 24;

  // Deduplicate by text — keep the most recent entry per unique text
  const dedupMap = new Map<string, any>();
  for (const t of raw) {
    const ex = dedupMap.get(t.text);
    if (!ex || new Date(t.created_at) > new Date(ex.created_at)) dedupMap.set(t.text, t);
  }
  const deduped = Array.from(dedupMap.values());

  return deduped
    .map((task) => {
      const createdMs = new Date(task.created_at).getTime();
      const daysElapsed = Math.floor((nowMs - createdMs) / ONE_DAY_MS);

      const isExpired =
        task.days_remaining !== null &&
        task.days_remaining > 0 &&
        daysElapsed >= task.days_remaining;

      const remainingDays =
        task.days_remaining !== null && task.days_remaining > 0
          ? Math.max(0, task.days_remaining - daysElapsed)
          : null;

      const updatedDate = task.updated_at
        ? new Date(task.updated_at).toISOString().split("T")[0]
        : null;
      const doneToday = task.done === true && updatedDate === today;

      return { ...task, done: doneToday, isExpired, remainingDays };
    })
    .filter((task) => !task.isExpired);
}

function computeStreak(rawTasks: any[]): number {
  const doneDates = new Set(
    rawTasks
      .filter((t) => t.done && t.updated_at)
      .map((t) => new Date(t.updated_at).toISOString().split("T")[0])
  );
  let streak = 0;
  const today = new Date();
  for (let d = 0; d < 30; d++) {
    const date = new Date(today);
    date.setDate(today.getDate() - d);
    const key = date.toISOString().split("T")[0];
    if (doneDates.has(key)) {
      streak++;
    } else {
      break;
    }
  }
  return streak;
}

const TasksPage = () => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const bn = language === "bn";
  const [tasks, setTasks] = useState<any[]>([]);
  const [rawTasks, setRawTasks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [burstId, setBurstId] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    const load = async () => {
      const { data } = await supabase
        .from("tasks")
        .select("*")
        .eq("patient_id", user.id)
        .order("created_at", { ascending: false });
      const raw = data ?? [];
      setRawTasks(raw);
      const processed = processTasks(raw);
      setTasks(processed);
      scheduleSmartReminders(processed);
      setLoading(false);
    };
    load();
  }, [user]);

  const toggle = useCallback(
    async (id: string) => {
      const task = tasks.find((t) => t.id === id);
      if (!task) return;
      const newDone = !task.done;
      const now = new Date().toISOString();
      setTasks((prev) =>
        prev.map((t) =>
          t.id === id ? { ...t, done: newDone, updated_at: now } : t
        )
      );
      setRawTasks((prev) =>
        prev.map((t) =>
          t.id === id ? { ...t, done: newDone, updated_at: now } : t
        )
      );
      if (newDone) setBurstId(id);
      const { error } = await supabase
        .from("tasks")
        .update({ done: newDone, updated_at: now })
        .eq("id", id);
      if (error) {
        setTasks((prev) => prev.map((t) => t.id === id ? { ...t, done: !newDone } : t));
        setRawTasks((prev) => prev.map((t) => t.id === id ? { ...t, done: !newDone } : t));
        toast.error(error.message);
      }
    },
    [tasks]
  );

  const completed = tasks.filter((t) => t.done).length;
  const total = tasks.length;
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
  const riskLevel = pct >= 75 ? "Low" : pct >= 50 ? "Medium" : "High";
  const riskColor =
    pct >= 75 ? "text-success" : pct >= 50 ? "text-warning" : "text-destructive";
  const streak = computeStreak(rawTasks);

  if (loading) {
    return (
      <div className="min-h-screen gradient-bg flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen gradient-bg pb-28">
      <div className="gradient-mint px-5 pt-12 pb-8 rounded-b-[2rem]">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-primary-foreground text-xl font-extrabold mb-1">
              {t("tasks.title")}
            </h1>
            <p className="text-primary-foreground/80 text-sm">
              {completed}/{total} {t("tasks.completed")}
            </p>
          </div>
          {streak > 0 && (
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="glass rounded-xl px-3 py-1.5 flex items-center gap-1.5"
            >
              <span className="text-base">🔥</span>
              <div>
                <p className="text-primary-foreground text-xs font-extrabold leading-none">
                  {streak} {bn ? "দিন" : "day"}
                </p>
                <p className="text-primary-foreground/70 text-[9px]">
                  {bn ? "স্ট্রিক" : "streak"}
                </p>
              </div>
            </motion.div>
          )}
        </div>
      </div>

      <div className="px-5 -mt-5">
        <div className="glass rounded-2xl p-4 shadow-float">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-bold">{t("dashboard.compliance")}</span>
            <span className={`text-sm font-extrabold ${riskColor}`}>{pct}%</span>
          </div>
          <div className="w-full h-3 bg-muted rounded-full overflow-hidden">
            <motion.div
              className="h-full rounded-full gradient-primary"
              initial={{ width: 0 }}
              animate={{ width: `${pct}%` }}
              transition={{ duration: 1, ease: "easeOut" }}
            />
          </div>
          <div className="flex items-center gap-2 mt-3">
            <AlertTriangle className={`h-4 w-4 ${riskColor}`} />
            <span className={`text-xs font-bold ${riskColor}`}>
              {riskLevel} {bn ? "ড্রপ-অফ রিস্ক" : "Drop-Off Risk"}
            </span>
          </div>
        </div>
      </div>

      <div className="px-5 mt-5 space-y-5">
        {tasks.length === 0 && (
          <motion.div
            variants={fadeUp}
            className="flex flex-col items-center justify-center py-16 gap-4"
          >
            <div className="h-20 w-20 rounded-full bg-primary/10 flex items-center justify-center">
              <ClipboardList className="h-10 w-10 text-primary/50" />
            </div>
            <div className="text-center">
              <p className="text-sm font-bold text-foreground mb-1">
                {bn ? "এখনো কোনো টাস্ক নেই" : "No tasks yet"}
              </p>
              <p className="text-xs text-muted-foreground mb-4">
                {bn
                  ? "আপনার ডাক্তার বা ডায়েটিশিয়ান টাস্ক যোগ করবেন"
                  : "Your doctor or dietitian will assign tasks here"}
              </p>
              <Link
                to="/find-dietitian"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-primary text-primary-foreground text-xs font-bold shadow-glow"
              >
                {bn ? "ডাক্তার বা ডায়েটিশিয়ান খুঁজুন" : "Find a Doctor or Dietitian"}
              </Link>
            </div>
          </motion.div>
        )}

        {(["morning", "midday", "evening", "general"] as const).map((slot) => {
          const slotTasks = tasks.filter((t) => getSlot(t.text) === slot);
          if (slotTasks.length === 0) return null;
          const meta = SLOT_META[slot];
          const SlotIcon = meta.Icon;
          return (
            <div key={slot}>
              <div className={`flex items-center gap-2 mb-2 px-1`}>
                <div className={`h-6 w-6 rounded-lg ${meta.bg} flex items-center justify-center`}>
                  <SlotIcon className={`h-3.5 w-3.5 ${meta.color}`} />
                </div>
                <span className={`text-xs font-extrabold uppercase tracking-wide ${meta.color}`}>
                  {bn ? meta.labelBn : meta.labelEn}
                </span>
              </div>
              <motion.div className="space-y-2" variants={stagger} initial="hidden" animate="show">
                {slotTasks.map((task) => (
                  <motion.button
                    key={task.id}
                    variants={fadeUp}
                    onClick={() => toggle(task.id)}
                    className={`w-full glass rounded-xl p-3.5 flex items-center gap-3 shadow-float text-left transition-all relative ${task.done ? "opacity-60" : ""}`}
                    whileTap={{ scale: 0.97 }}
                  >
                    <div className={`h-8 w-8 rounded-lg ${meta.bg} flex items-center justify-center shrink-0`}>
                      <SlotIcon className={`h-4 w-4 ${meta.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className={`text-sm font-semibold ${task.done ? "line-through text-muted-foreground" : "text-foreground"}`}>
                        {displayText(bn && task.text_bn ? task.text_bn : task.text)}
                      </span>
                      {task.remainingDays !== null && task.remainingDays > 0 && (
                        <p className="text-[10px] text-muted-foreground mt-0.5">
                          {task.remainingDays} {bn ? "দিন বাকি" : "days left"}
                        </p>
                      )}
                      {task.remainingDays === 0 && (
                        <p className="text-[10px] text-destructive mt-0.5 font-medium">
                          {bn ? "আজই শেষ দিন" : "Last day"}
                        </p>
                      )}
                    </div>
                    <div className="relative">
                      <AnimatePresence>
                        {burstId === task.id && (
                          <ConfettiBurst onDone={() => setBurstId(null)} />
                        )}
                      </AnimatePresence>
                      {task.done ? (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: [0, 1.3, 1] }}
                          transition={{ type: "spring", stiffness: 500, damping: 15 }}
                        >
                          <CheckCircle2 className="h-5 w-5 text-success" />
                        </motion.div>
                      ) : (
                        <Circle className="h-5 w-5 text-muted-foreground" />
                      )}
                    </div>
                  </motion.button>
                ))}
              </motion.div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TasksPage;
